import type { CSSProperties } from 'react';
import { interpolate } from 'remotion';

const clampInterpolate = (
  value: number,
  input: number[],
  output: number[],
): number =>
  interpolate(value, input, output, {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

export const calculateSsulImageEntranceStyle = (
  fx: string,
  elapsedFrames: number,
  fps: number,
): { style: CSSProperties; overlayOpacity: number } => {
  if (fx === 'none') {
    return {
      style: {
        opacity: 1,
        transform: 'translate(0px, 0px) scale(1)',
        filter: 'none',
      },
      overlayOpacity: 0,
    };
  }
  const progress = clampInterpolate(
    elapsedFrames,
    [0, Math.round(0.36 * fps)],
    [0, 1],
  );

  if (fx === 'slide-left') {
    return {
      style: {
        opacity: progress,
        transform: `translate(${(1 - progress) * 38}px, 0px) scale(${
          0.985 + progress * 0.015
        })`,
        filter: `blur(${(1 - progress) * 2.5}px)`,
      },
      overlayOpacity: 0,
    };
  }
  if (fx === 'slide-right') {
    return {
      style: {
        opacity: progress,
        transform: `translate(${(progress - 1) * 38}px, 0px) scale(${
          0.985 + progress * 0.015
        })`,
        filter: `blur(${(1 - progress) * 2.5}px)`,
      },
      overlayOpacity: 0,
    };
  }
  if (fx === 'pop') {
    return {
      style: {
        opacity: progress,
        transform: `translate(0px, 0px) scale(${0.92 + progress * 0.08})`,
        filter: 'blur(0px)',
      },
      overlayOpacity: 0,
    };
  }
  if (fx === 'blur-in') {
    return {
      style: {
        opacity: progress,
        transform: 'translate(0px, 0px) scale(1)',
        filter: `blur(${(1 - progress) * 7}px)`,
      },
      overlayOpacity: 0,
    };
  }
  if (fx === 'impact-pop') {
    const scale = clampInterpolate(
      progress,
      [0, 0.55, 0.78, 1],
      [0.72, 1.16, 0.98, 1],
    );
    const y = clampInterpolate(progress, [0, 1], [42, 0]);
    const shakeX =
      Math.sin(elapsedFrames * 1.8) *
      clampInterpolate(progress, [0, 1], [8, 0]);
    const flash = clampInterpolate(progress, [0, 0.2, 0.55], [0.5, 0.2, 0]);
    return {
      style: {
        opacity: progress,
        transform: `translate(${shakeX}px, ${y}px) scale(${scale})`,
        filter: `brightness(${1 + (1 - progress) * 0.16}) blur(${
          (1 - progress) * 2.5
        }px)`,
      },
      overlayOpacity: flash,
    };
  }
  if (fx === 'zoom-blur') {
    return {
      style: {
        opacity: progress,
        transform: `translate(0px, 0px) scale(${1.22 - progress * 0.22})`,
        filter: `brightness(${1 + (1 - progress) * 0.14}) blur(${
          (1 - progress) * 11
        }px)`,
      },
      overlayOpacity: 0,
    };
  }
  if (fx === 'whip-slide') {
    const x = clampInterpolate(progress, [0, 1], [110, 0]);
    const rotate = clampInterpolate(progress, [0, 1], [2.5, 0]);
    return {
      style: {
        opacity: progress,
        transform: `translate(${x}px, 0px) rotate(${rotate}deg) scale(1.04)`,
        filter: `blur(${(1 - progress) * 8}px)`,
      },
      overlayOpacity: 0,
    };
  }
  if (fx === 'bounce-drop') {
    const y = clampInterpolate(progress, [0, 0.72, 1], [-76, 10, 0]);
    const scale = clampInterpolate(progress, [0, 0.72, 1], [1.08, 0.98, 1]);
    return {
      style: {
        opacity: progress,
        transform: `translate(0px, ${y}px) scale(${scale})`,
        filter: `blur(${(1 - progress) * 4}px)`,
      },
      overlayOpacity: 0,
    };
  }
  return {
    style: {
      opacity: progress,
      transform: `translate(0px, ${(1 - progress) * 34}px) scale(1)`,
      filter: `blur(${(1 - progress) * 2}px)`,
    },
    overlayOpacity: 0,
  };
};

export const calculateSsulOverlayStyle = (
  fx: string | null,
  elapsedFrames: number,
  fps: number,
  globalFrame: number,
): CSSProperties | null => {
  if (fx === 'film-grain') {
    const grainShiftX = (globalFrame * 7) % 17;
    const grainShiftY = (globalFrame * 11) % 19;
    const flickerOpacity = 0.58 + ((globalFrame * 13) % 5) * 0.02;
    return {
      opacity: flickerOpacity,
      backgroundImage:
        'repeating-linear-gradient(0deg, rgba(24,12,4,0.1) 0 1px, rgba(255,244,214,0.04) 1px 3px), repeating-radial-gradient(circle at 30% 20%, rgba(255,248,222,0.58) 0 0.8px, rgba(29,14,4,0.38) 0.8px 1.7px, transparent 1.7px 4px), linear-gradient(118deg, rgba(255,154,64,0.24), rgba(78,37,10,0.04) 44%, rgba(18,84,118,0.18))',
      backgroundSize: '100% 4px, 11px 13px, 100% 100%',
      backgroundPosition: `0 ${grainShiftY}px, ${grainShiftX}px ${grainShiftY}px, 0 0`,
      backgroundBlendMode: 'soft-light, overlay, color',
      mixBlendMode: 'overlay',
      backdropFilter: 'contrast(1.16) saturate(0.78) sepia(0.18)',
      boxShadow: 'inset 0 0 72px rgba(52,24,8,0.24)',
    };
  }
  if (fx === 'soft-vignette') {
    return {
      opacity: 1,
      background:
        'radial-gradient(circle at center, transparent 45%, rgba(16,10,4,0.42) 100%)',
      mixBlendMode: 'multiply',
    };
  }
  if (fx === 'white-flash') {
    const safeElapsedFrames = Math.max(0, elapsedFrames);
    const sweepFrames = Math.max(1, Math.round(2.4 * fps));
    const sweepProgress = (safeElapsedFrames % sweepFrames) / sweepFrames;
    const sweepStart = -30 + sweepProgress * 134;
    const flashOpacity = clampInterpolate(
      safeElapsedFrames,
      [
        0,
        Math.max(1, Math.round(0.12 * fps)),
        Math.max(2, Math.round(0.55 * fps)),
      ],
      [0.48, 0.2, 0],
    );
    return {
      opacity: 1,
      backgroundImage: `linear-gradient(rgba(255,255,255,${flashOpacity}), rgba(255,255,255,${flashOpacity})), linear-gradient(112deg, transparent ${sweepStart}%, rgba(255,213,142,0.2) ${sweepStart + 7}%, rgba(255,255,255,0.72) ${sweepStart + 16}%, rgba(255,182,86,0.28) ${sweepStart + 25}%, transparent ${sweepStart + 38}%), radial-gradient(circle at ${16 + sweepProgress * 68}% 23%, rgba(255,177,70,0.4), rgba(255,228,178,0.12) 24%, transparent 52%), linear-gradient(180deg, rgba(255,178,82,0.1), rgba(73,24,0,0.05))`,
      backgroundBlendMode: 'screen, screen, screen, soft-light',
      mixBlendMode: 'screen',
      backdropFilter: 'saturate(1.1) brightness(1.03)',
      boxShadow: 'inset 0 0 64px rgba(255,151,48,0.14)',
    };
  }
  if (fx === 'subtle-grain') {
    return {
      opacity: 0.15,
      backgroundImage:
        'repeating-radial-gradient(circle at 30% 20%, rgba(255,255,255,0.35) 0 1px, rgba(0,0,0,0.22) 1px 2px, transparent 2px 4px)',
      backgroundSize: '9px 9px',
      backgroundPosition: `${globalFrame % 5}px ${(globalFrame * 3) % 7}px`,
      mixBlendMode: 'overlay',
    };
  }
  if (fx === 'camera-flash') {
    return {
      opacity: clampInterpolate(
        Math.max(0, elapsedFrames),
        [0, Math.max(1, Math.round(0.22 * fps))],
        [0.72, 0],
      ),
      background: '#FFFFFF',
      mixBlendMode: 'screen',
    };
  }
  if (fx === 'shimmer-sweep') {
    const safeElapsedFrames = Math.max(0, elapsedFrames);
    const periodFrames = Math.max(1, Math.round(3 * fps));
    const sweepFrames = Math.max(1, Math.round(periodFrames * 0.65));
    const cycleFrame = safeElapsedFrames % periodFrames;
    const sweepProgress = clampInterpolate(
      cycleFrame,
      [0, sweepFrames],
      [0, 1],
    );
    return {
      opacity: 0.25,
      backgroundImage:
        'linear-gradient(110deg, transparent 45.8%, rgba(255,255,255,0.92) 50%, transparent 54.2%)',
      backgroundSize: '300% 100%',
      backgroundPosition: `${(1 - sweepProgress) * 100}% 0%`,
      backgroundRepeat: 'no-repeat',
      mixBlendMode: 'screen',
    };
  }
  return null;
};
